import moment from 'moment-timezone'

import {getDateFormat, getTimeFormat} from '@base/app-services/i18n-format'

import {DATE_TIME_FORMAT} from './dateTimeFormat'
import {LOCAL_TIMEZONE} from './timezoneMapping'
import type {TimezoneType} from './timezoneMapping'
import type {TZStringType} from './TZStringType'

type FormattedDateTime = {
  date: string | undefined | null
  time: string | undefined | null
}

function parse(rawDateTime: string, timezone: TimezoneType) {
  if (timezone === LOCAL_TIMEZONE) {
    return moment(rawDateTime, DATE_TIME_FORMAT)
  }
  return moment.tz(rawDateTime, timezone)
}

export default function format(
  rawDateTime: string,
  timezone: TimezoneType = 'UTC'
): FormattedDateTime {
  if (!rawDateTime) {
    return {
      date: null,
      time: null,
    }
  }

  const dateFormat = getDateFormat()
  const timeFormat = getTimeFormat()

  const parsed = parse(rawDateTime, timezone)

  return {
    date: dateFormat ? parsed.format(dateFormat) : null,
    time: timeFormat ? parsed.format(timeFormat) : null,
  }
}

export function getLocalTimezone(): TZStringType {
  // @ts-ignore Ignored during migration
  if (moment.defaultZone) {
    // @ts-ignore Ignored during migration
    return moment.defaultZone.name
  }

  // @ts-ignore Ignored during migration
  return moment.tz.guess()
}

// @ts-ignore Ignored during migration
function sameDay(startDate: moment, endDate: moment): boolean {
  return startDate.isSame(endDate, 'day')
}

function formatTimePeriodAsSentenceWithoutTime(
  // @ts-ignore Ignored during migration
  startDate: moment,
  // @ts-ignore Ignored during migration
  endDate: moment
): string {
  const startDateAsString = startDate.format(getDateFormat())
  const endDateAsString = endDate.format(getDateFormat())

  if (sameDay(startDate, endDate)) {
    return startDateAsString
  }

  return `${startDateAsString}-${endDateAsString}`
}

function formatTimePeriodAsSentenceWithTime(
  // @ts-ignore Ignored during migration
  startDate: moment,
  // @ts-ignore Ignored during migration
  endDate: moment
): string {
  const startDateAsString = startDate.format(getDateFormat())
  const startTimeAsString = startDate.format(getTimeFormat())
  const endDateAsString = endDate.format(getDateFormat())
  const endTimeAsString = endDate.format(getTimeFormat())

  if (sameDay(startDate, endDate)) {
    return `${startDateAsString} ${startTimeAsString} - ${endTimeAsString}`
  }

  return `${startDateAsString} ${startTimeAsString} - ${endDateAsString} ${endTimeAsString}`
}

// @ts-ignore Ignored during migration
function maybeParseTimeToTimezone(time: string, skipTime: boolean): moment {
  if (skipTime) {
    return moment(time).utc()
  }

  return moment.tz(time, getLocalTimezone())
}

export function formatTimePeriodAsSentence(
  startDate: string,
  endDate: string,
  skipTime: boolean,
  skipTimezone: boolean = false
): string {
  // @ts-ignore Ignored during migration
  const parsedStartDate: moment = maybeParseTimeToTimezone(
    startDate,
    skipTimezone
  )
  // @ts-ignore Ignored during migration
  const parsedEndDate: moment = maybeParseTimeToTimezone(endDate, skipTimezone)

  if (skipTime) {
    return formatTimePeriodAsSentenceWithoutTime(parsedStartDate, parsedEndDate)
  }

  return formatTimePeriodAsSentenceWithTime(parsedStartDate, parsedEndDate)
}
